import { LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { requestApi, parseResponse } from '../../../services/api.js';
import { createFilterPillsHelper, isBonus } from '../shop-utils';
import { AFFORDABLE, BONUSES, GOLDEN_CHIP, FREESPIN } from '../../../constants/index.js';

import {
	clearPaymentSpec,
	closeDetailView,
	MARKETPLACE_REDEEMED_SHOP_ITEM
} from '../../marketplace/marketplace-duck.js';
import imageFormatHandler from '../../utilities/image-format';
import { REMAINING_GERMANY_BONUS_VALUE_UPDATE } from '~/components/account/ducks';
import { itemCostHelper } from '~/components/marketplace/marketplace-utils.js';
import { REDUCER_NAME } from '../shop-constants';

const FAILURE = 'FAILURE';

const host = '/';

export const REMOVE_ITEM = '@komigen/shop/remove_item';
export const FETCH_SHOP_ITEMS_START = '@komigen/shop/fetch_shop_items_start';
export const HYDRATE_SHOP_ITEMS = '@komigen/shop/hydrate_shop_items';
export const SHOP_TRANSLATIONS_UPDATE = '@komigen/shop/shop_translations_update';
export const FILTER_SHOP_ITEMS = '@komigen/shop/filter_shop_items';
export const CREATE_FILTER_PILLS = '@komigen/shop/create_filter_pills';
export const PLAYER_LEVEL_UPDATE = '@komigen/shop/player_level_update';
export const PLAYER_SEGMENTATION = '@komigen/shop/player_segmentation';
export const SHOP_TIER_SEGMENTATION_LEVEL_FAILURE = '@komigen/shop/level_failure';
export const RESET_SHOP_AVAILABILITY_HYDRATED = '@komigen/shop/reset_shop_availability_hydrated';

const initialState = {
	hydrated: false,
	shopItems: [], // shopItems may change depending on if items are filtered.
	allShopItems: [], // Will not change on filtering.
	pills: null,
	playerLevel: null,
	playerSegmentation: null
};

const reducer = (state = initialState, { type, payload } = {}) => {
	switch (type) {
		case REMOVE_ITEM: {
			return {
				...state,
				shopItems: state.shopItems.filter(shopItem => shopItem.id !== payload),
				allShopItems: state.allShopItems.filter(shopItem => shopItem.id !== payload)
			};
		}
		case MARKETPLACE_REDEEMED_SHOP_ITEM: {
			return {
				...state,
				shopItems: state.shopItems.filter(shopItem => shopItem.id !== payload),
				allShopItems: state.allShopItems.filter(shopItem => shopItem.id !== payload)
			};
		}
		case FETCH_SHOP_ITEMS_START: {
			return {
				...state,
				hydrated: false,
				shopItems: []
			};
		}
		case HYDRATE_SHOP_ITEMS: {
			return {
				...state,
				hydrated: true,
				shopItems: payload.shopItems,
				allShopItems: payload.shopItems
			};
		}
		case SHOP_TRANSLATIONS_UPDATE: {
			return {
				...state,
				translations: {
					...payload
				}
			};
		}
		case FILTER_SHOP_ITEMS: {
			return {
				...state,
				shopItems: payload
			};
		}
		case CREATE_FILTER_PILLS: {
			return {
				...state,
				pills: payload
			};
		}
		case REMAINING_GERMANY_BONUS_VALUE_UPDATE: {
			return {
				...state,
				hydrated: false
			};
		}
		case PLAYER_LEVEL_UPDATE: {
			return {
				...state,
				playerLevel: payload
			};
		}
		case PLAYER_SEGMENTATION: {
			return {
				...state,
				playerSegmentation: payload
			};
		}
		case SHOP_TIER_SEGMENTATION_LEVEL_FAILURE: {
			return {
				...state,
				playerSegmentation: 'failure'
			};
		}
		case RESET_SHOP_AVAILABILITY_HYDRATED: {
			return {
				...state,
				hydrated: false
			};
		}
		case LOGOUT: {
			return {};
		}
		default: {
			return state;
		}
	}
};

export const removeItem =
	(id, forceClose = true) =>
	(dispatch, getState) => {
		if (forceClose) {
			const {
				marketplace: { currentItemDetails }
			} = getState();
			if (currentItemDetails && currentItemDetails.id === id) {
				dispatch(clearPaymentSpec());
				dispatch(closeDetailView());
			}
		}
		dispatch({
			type: REMOVE_ITEM,
			payload: id
		});
	};

export const removeItemByBonusId = internalBonusId => (dispatch, getState) => {
	const items = getState().shop.shopItems;
	const foundItem = items.find(item => internalBonusId === item.id);
	if (foundItem) {
		dispatch(removeItem(foundItem.id));
	}
};

export const shopTranslationsUpdate = translations => {
	return {
		type: SHOP_TRANSLATIONS_UPDATE,
		payload: translations
	};
};

export const hydrateShop =
	() =>
	async (dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		dispatch({
			type: FETCH_SHOP_ITEMS_START
		});

		const shopItems = parseResponse(await requestApi(`${host}rest/state/shopitems`));

		const shopItemsWithImageFormat = isPictureElementEnabled
			? imageFormatHandler({ imageDescriptors, imageFormats })(shopItems)
			: shopItems;

		const sortedshopItemsWithImageFormat = shopItemsWithImageFormat
			.sort((a, b) => b.id - a.id)
			.sort((a, b) => a.sortOrder - b.sortOrder)
			.filter(item => item !== null);

		const payload = {
			shopItems: sortedshopItemsWithImageFormat
		};

		return dispatch({
			type: HYDRATE_SHOP_ITEMS,
			payload
		});
	};

export const shopPlayerSegmentation = () => {
	return async dispatch => {
		try {
			const { status, result } = await requestApi('/player/shop/segmentation');
			if (status === FAILURE) {
				return dispatch({ type: SHOP_TIER_SEGMENTATION_LEVEL_FAILURE });
			}
			dispatch({
				type: PLAYER_SEGMENTATION,
				payload: result
			});
		} catch (error) {
			return dispatch({ type: SHOP_TIER_SEGMENTATION_LEVEL_FAILURE });
		}
	};
};

export const filterShopItems =
	(filter = '') =>
	(dispatch, getState) => {
		// If a pill is clicked and we want to filter the shopItems accordingly
		const { allShopItems } = getState().shop;
		const { points } = getState().account;
		if (filter === 'showAllItems') {
			return dispatch({ type: FILTER_SHOP_ITEMS, payload: allShopItems });
		}
		if (filter === AFFORDABLE) {
			const affordableItems = allShopItems.filter(item => points >= itemCostHelper(item));
			return dispatch({
				type: FILTER_SHOP_ITEMS,
				payload: affordableItems
			});
		}

		if (filter === FREESPIN) {
			const freespinItems = allShopItems.filter(item => item.type === FREESPIN);
			return dispatch({
				type: FILTER_SHOP_ITEMS,
				payload: freespinItems
			});
		}

		if (filter === GOLDEN_CHIP) {
			const goldenChipItems = allShopItems.filter(item => item.type === GOLDEN_CHIP);
			return dispatch({
				type: FILTER_SHOP_ITEMS,
				payload: goldenChipItems
			});
		}

		// Treat DepositBonuses and FreeMoneyBonuses as "Bonuses" - they go under the same category.
		const filtered = allShopItems.filter(
			item => (filter === BONUSES && isBonus(item.type)) || item.type === filter
		);

		return dispatch({
			type: FILTER_SHOP_ITEMS,
			payload: filtered
		});
	};

export const createFilterPills = (shopItems, points, translations) => dispatch => {
	const { SHOP_FILTER_PILL_AFFORDABLE, SHOP_FILTER_PILL_BONUSES } = translations;

	const pills = createFilterPillsHelper(shopItems, points, SHOP_FILTER_PILL_AFFORDABLE, SHOP_FILTER_PILL_BONUSES);

	return dispatch({
		type: CREATE_FILTER_PILLS,
		payload: pills
	});
};

export const playerLevelUpdate = level => {
	return {
		type: PLAYER_LEVEL_UPDATE,
		payload: level
	};
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
